import { FULFILLMENT_TYPES, FULFILLMENT_STATUSES, STATUS_CODES } from '../constants';
import { IActiveDistribution, IActiveOrdersLineItem } from '../models/active-orders';

/**
 * Readers for an OMS `ActiveDistribution`.
 *
 * These replace the adapter that used to rebuild every distribution into the
 * older fulfillment-group shape. Nothing here constructs a new object: each
 * function answers one question about the distribution it is handed, so the
 * API type flows through the components unchanged.
 */

export const DISTRIBUTION_TYPES = {
  PICKUP: 'PICKUP',
  DELIVERY: 'DELIVERY',
};

/**
 * OMS reports status in its own casing ("DELIVERED", "Ready for pick up").
 * Everything downstream looks statuses up by the exact strings in
 * `FULFILLMENT_STATUSES`, so normalize first. Anything unrecognized passes
 * through untouched and falls to the default status.
 *
 * `ActiveDistribution.status` is a bare String in the OMS schema with no enum
 * behind it, and OMS passes it through from two different upstreams: a pickup
 * carries CPS's `statusDetails.status`, a delivery carries the delivery
 * tracker's `ExecutionGroup.status`. The two do not share a vocabulary or a
 * casing convention, which is why `Ready` and `InTransit` sit here next to
 * `READY FOR PICK UP`.
 */
const STATUS_ALIASES: { [key: string]: string } = {
  DELIVERED: FULFILLMENT_STATUSES.DELIVERED,
  SHIPPED: FULFILLMENT_STATUSES.SHIPPED,
  'OUT FOR DELIVERY': FULFILLMENT_STATUSES.OUT_FOR_DELIVERY,
  OUT_FOR_DELIVERY: FULFILLMENT_STATUSES.OUT_FOR_DELIVERY,
  'READY FOR PICK UP': FULFILLMENT_STATUSES.READY_FOR_PICKUP,
  'READY FOR PICKUP': FULFILLMENT_STATUSES.READY_FOR_PICKUP,
  READY_FOR_PICKUP: FULFILLMENT_STATUSES.READY_FOR_PICKUP,
  'ON ITS WAY TO STORE': FULFILLMENT_STATUSES.ON_ITS_WAY_TO_STORE,
  ON_ITS_WAY_TO_STORE: FULFILLMENT_STATUSES.ON_ITS_WAY_TO_STORE,
  'ON SCHEDULE': FULFILLMENT_STATUSES.ON_SCHEDULE,
  ON_SCHEDULE: FULFILLMENT_STATUSES.ON_SCHEDULE,
  /** CPS sends this for a pickup that has arrived and is waiting. */
  READY: FULFILLMENT_STATUSES.READY_FOR_PICKUP,
  'BEING PROCESSED': FULFILLMENT_STATUSES.BEING_PROCESSED,
  BEING_PROCESSED: FULFILLMENT_STATUSES.BEING_PROCESSED,
  CANCELED: FULFILLMENT_STATUSES.CANCELED,
  CANCELLED: FULFILLMENT_STATUSES.CANCELED,
  RETURNED: FULFILLMENT_STATUSES.RETURNED,
};

/**
 * Statuses that mean different things depending on where the goods are headed.
 * CPS sends one `InTransit` for both cases: on a pickup the item is moving to
 * the store, on a delivery it has shipped to the customer.
 *
 * Without a distribution type there is no way to tell those apart, so an
 * ambiguous status passes through rather than guess at one of them.
 */
const STATUS_ALIASES_BY_DISTRIBUTION_TYPE: {
  [status: string]: { [distributionType: string]: string };
} = {
  INTRANSIT: {
    [DISTRIBUTION_TYPES.PICKUP]: FULFILLMENT_STATUSES.ON_ITS_WAY_TO_STORE,
    [DISTRIBUTION_TYPES.DELIVERY]: FULFILLMENT_STATUSES.SHIPPED,
  },
};

export const normalizeStatus = (
  status?: string,
  distributionType?: string
): string | undefined => {
  const trimmed = status?.trim();
  if (!trimmed) return undefined;
  const key = trimmed.toUpperCase();
  const byDistributionType = distributionType
    ? STATUS_ALIASES_BY_DISTRIBUTION_TYPE[key]?.[distributionType]
    : undefined;
  return byDistributionType ?? STATUS_ALIASES[key] ?? trimmed;
};

/** The status the cards, drawer, and tracker all read. */
export const getDistributionStatus = (distribution?: IActiveDistribution) => {
  return normalizeStatus(distribution?.status, distribution?.distributionType);
};

export const isPickup = (distribution?: IActiveDistribution) => {
  return distribution?.distributionType === DISTRIBUTION_TYPES.PICKUP;
};

/**
 * The UI branches on seven granular fulfillment types. `activeOrders` only says
 * PICKUP or DELIVERY, so a card lands on one of the two most common ones.
 *
 * The drawer does not use this directly: it reads `orderDetails`, whose
 * `FulfillmentGroup.fulfillmentType` already names the granular type. Use
 * `resolveFulfillmentType` there, which falls back to this.
 */
export const getFulfillmentType = (distribution?: IActiveDistribution) => {
  return isPickup(distribution)
    ? FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE
    : FULFILLMENT_TYPES.SHIP_TO_HOME;
};

/**
 * The exact strings `FulfillmentGroup.fulfillmentType` can carry, from
 * `OVQFulfillmentTypeConstants` in `order-management-svc`:
 *
 *   ShipToHome, BuyOnlineShipToStore, BuyOnlinePickupInStore,
 *   BuyOnlineDeliverFromStore, DFS, Appliance, MISC, InStorePurchase,
 *   unavailable, WillCallFulfillment, Additional Items
 *
 * OMS sets the field in `GroupAndSortingHelper` (`populateWC()` and friends)
 * via `setFulfillmentType(...)`; `OrderDetailsConstants` holds the same strings
 * as its `*_INDICATOR` constants. There is no short-code vocabulary on this
 * field — no STH, BOSS, BOPIS or MA — so most of these values are already the
 * values in `FULFILLMENT_TYPES` and pass straight through.
 *
 * Matching is exact. Upper-casing the value would break `ShipToHome` and
 * `unavailable`, both of which OMS sends in mixed or lower case.
 */
const FULFILLMENT_TYPE_VALUES: string[] = Object.values(FULFILLMENT_TYPES);

/**
 * The one value OMS sends that is not already a `FULFILLMENT_TYPES` value.
 * `DFS` is the in-store-originated twin of `BuyOnlineDeliverFromStore`; the UI
 * draws the two identically, so they share a type.
 */
const FULFILLMENT_TYPE_ALIASES: { [fulfillmentType: string]: string } = {
  DFS: FULFILLMENT_TYPES.BUY_ONLINE_DELIVER_FROM_STORE,
};

/**
 * The granular type from `orderDetails`, falling back to the coarse one the
 * card already knows.
 *
 * `InStorePurchase`, `unavailable` and `Additional Items` are real OMS values
 * with no UI type behind them, and OMS is free to add more. They take the
 * fallback: a missing or unrecognized value answers the PICKUP/DELIVERY
 * reading of the same order rather than `undefined`, which left the tracker
 * with no progress bar to draw at all.
 *
 * `WillCallFulfillment` reaches this and is returned as-is, so the will-call
 * arms of `getProgressBarData` and `STEPS_BY_FULFILLMENT_TYPE` are live. OMS
 * gates it behind the `WC_SWITCH` feature switch, which is on in Beta and
 * Prod.
 *
 * Locker eligibility used to be a third consumer of the will-call value. It no
 * longer reads `fulfillmentType` at all — `storePickup.IsInLocker` answers it —
 * so the tracker is the only caller that still needs the fallback below.
 */
export const resolveFulfillmentType = (
  fulfillmentType?: string | null,
  distribution?: IActiveDistribution
) => {
  const value = fulfillmentType?.trim();
  if (value && FULFILLMENT_TYPE_VALUES.includes(value)) return value;
  return (value ? FULFILLMENT_TYPE_ALIASES[value] : undefined) ?? getFulfillmentType(distribution);
};

/**
 * The drawer reads status from the numeric `statusCode` rather than the string
 * `ActiveDistribution.status`, which is a bare String forwarding two different
 * upstream vocabularies. The cards still read the string, so `STATUS_ALIASES`
 * stays.
 *
 * Every code in `STATUS_CODES` is mapped. An unmapped one reached
 * `TrackingStepper` as `status=""`, which draws no progress at all — a
 * delivered ship-to-home order and an on-schedule appliance order both showed
 * an empty bar.
 *
 * The statuses are the nine in `FULFILLMENT_STATUSES`, so several codes share
 * one. Where a code names no status of its own, the mapping is the step the
 * progress bars already put it on (`tracking-utils.ts`):
 *
 * - 100/106: step 0, "Processed", like 101.
 * - 103 (`IN_TRANSIT`): step 1, "Shipped" on both bars that show it. The same
 *   reading as `STATUS_ALIASES`'s `INTRANSIT` for a delivery.
 * - 104/110 (attempted): step 2, "Out for Delivery" — the courier has the
 *   goods and will try again.
 * - 108/111/401: the on-schedule codes, and step 1 "Delivery Window
 *   Scheduled" on the appliance bar.
 * - 200/1000: the last step, "Delivered" on the delivery bars.
 * - 107 (`WORK_ORDER_CANCELED`) and 151 (`RETURN_IN_PROGRESS`): the terminal
 *   status their names give, alongside 140 and 150.
 *
 * 132 (`PREPARING_FOR_PICKUP`, meaning at the store being prepared) maps to
 * Being Processed rather than Ready for Pickup: it is not yet collectable.
 * OMS `OrderConstants` defines only 101 and 131, so whether 132 actually
 * arrives is unconfirmed — this mapping is chosen to fail in the safe
 * direction if it does.
 */
const STATUS_BY_CODE: { [code: number]: string } = {
  [STATUS_CODES.SEE_ORDER_DETAILS]: FULFILLMENT_STATUSES.BEING_PROCESSED,
  [STATUS_CODES.PROCESSING]: FULFILLMENT_STATUSES.BEING_PROCESSED,
  [STATUS_CODES.SHIPPED]: FULFILLMENT_STATUSES.SHIPPED,
  [STATUS_CODES.IN_TRANSIT]: FULFILLMENT_STATUSES.SHIPPED,
  [STATUS_CODES.ATTEMPTED]: FULFILLMENT_STATUSES.OUT_FOR_DELIVERY,
  [STATUS_CODES.DELIVERED]: FULFILLMENT_STATUSES.DELIVERED,
  [STATUS_CODES.BACKORDERED]: FULFILLMENT_STATUSES.BEING_PROCESSED,
  [STATUS_CODES.WORK_ORDER_CANCELED]: FULFILLMENT_STATUSES.CANCELED,
  [STATUS_CODES.MAJOR_APPLIANCE_ON_SCHEDULE]: FULFILLMENT_STATUSES.ON_SCHEDULE,
  [STATUS_CODES.OUT_FOR_DELIVERY]: FULFILLMENT_STATUSES.OUT_FOR_DELIVERY,
  [STATUS_CODES.DELIVERY_ATTEMPTED]: FULFILLMENT_STATUSES.OUT_FOR_DELIVERY,
  [STATUS_CODES.BODFS_ON_SCHEDULE]: FULFILLMENT_STATUSES.ON_SCHEDULE,
  [STATUS_CODES.ON_ITS_WAY_TO_STORE]: FULFILLMENT_STATUSES.ON_ITS_WAY_TO_STORE,
  [STATUS_CODES.READY_FOR_PICKUP]: FULFILLMENT_STATUSES.READY_FOR_PICKUP,
  [STATUS_CODES.PREPARING_FOR_PICKUP]: FULFILLMENT_STATUSES.BEING_PROCESSED,
  [STATUS_CODES.CANCELED]: FULFILLMENT_STATUSES.CANCELED,
  [STATUS_CODES.RETURNED]: FULFILLMENT_STATUSES.RETURNED,
  [STATUS_CODES.RETURN_IN_PROGRESS]: FULFILLMENT_STATUSES.RETURNED,
  [STATUS_CODES.COMPLETED]: FULFILLMENT_STATUSES.DELIVERED,
  [STATUS_CODES.DFS_ON_SCHEDULE]: FULFILLMENT_STATUSES.ON_SCHEDULE,
  [STATUS_CODES.DFS_DELIVERED]: FULFILLMENT_STATUSES.DELIVERED,
};

export const getStatusFromCode = (statusCode?: number) => {
  return statusCode === undefined ? undefined : STATUS_BY_CODE[statusCode];
};

/**
 * A stable identity for one line item, used to tell items apart when
 * consolidating. OMS sends `lineId`; the index is a fallback for the rare
 * response that omits it. Never rendered.
 */
export const getLineItemKey = (
  lineItem: IActiveOrdersLineItem,
  index: number,
  orderNumber?: string
) => {
  return lineItem?.lineId ?? `${orderNumber ?? ''}-${index}`;
};
